import React from 'react';
import FeatureFlags from '@/constants/system/FeatureFlags';
import { FEATURE_FLAGS } from '@/lib/constants/localStorageKeys';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import useOrgContext from './useOrgContext';

const getStorageKey = (flag) => `${FEATURE_FLAGS}_${flag}`;
const isBooleanString = (str) => str === 'true' || str === 'false';

const useFeatureFlags = (swrOptions) => {
  const ownerOrgId = useOwnerOrgId();
  const { data } = useOrgContext(ownerOrgId, swrOptions);

  const context = React.useMemo(() => {
    // If the user is a super admin, they will be able to have multiple
    // context objects - their base context and the context of the passed
    // `ownerOrgId`. Here we want to return the context of the owner orgId, but
    // default to the primary context in case they aren't super admins.
    if (!data?.context?.contexts?.length) return null;
    return (
      data.context.contexts.find(({ org }) => org.ownerOrgId === ownerOrgId) ||
      data.context.contexts[0]
    );
  }, [data, ownerOrgId]);

  const featureFlags = React.useMemo(() => {
    return Object.values(FeatureFlags).reduce((obj, flag) => {
      let flagValue = !!(context && context.org.featureFlags[flag]);
      const localStorageFlagValue = localStorage.getItem(getStorageKey(flag));

      if (isBooleanString(localStorageFlagValue)) {
        flagValue = localStorageFlagValue === 'true';
      }

      return {
        ...obj,
        [flag]: flagValue,
      };
    }, {});
  }, [context]);

  return featureFlags;
};

export default useFeatureFlags;
